import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaFileInvoice, FaEye, FaUpload, FaTruck,
  FaCalendarAlt, FaWeightHanging, FaDollarSign,
  FaClock, FaCheckCircle, FaTimes, FaSpinner,
  FaBuilding, FaMapMarkerAlt, FaFileAlt,
  FaDownload, FaPrint, FaLink, FaTruckLoading,
  FaMoneyBillWave, FaHourglassHalf, FaSync,
  FaPlus, FaTrash, FaInfoCircle, FaSearch, FaFilter,
  FaExternalLinkAlt, FaFilePdf
} from 'react-icons/fa';
import SubbieLayout from '../layouts/SubbieLayout';
import InvoicePDFButton from '../components/InvoicePDFButton';

function SubbieInvoices({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [pendingLoads, setPendingLoads] = useState([]);
  const [podSubmissions, setPodSubmissions] = useState([]);
  const [user, setUser] = useState({});
  const [subbieId, setSubbieId] = useState(null);
  const [counts, setCounts] = useState({ 
    pending: 0, 
    paid: 0, 
    readyForPOD: 0, 
    podPending: 0,
    total: 0 
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [showPaid, setShowPaid] = useState(false);
  
  // Modal states
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showPODModal, setShowPODModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Invoice form
  const [invoiceForm, setInvoiceForm] = useState({
    weight: '',
    rate: '',
    extras: [],
    paymentTerms: '30 Days',
    vatApplicable: true,
  });
  
  // POD form
  const [podForm, setPodForm] = useState({
    submissionType: 'courier',
    courierName: '',
    waybillNumber: '',
    trackingUrl: '',
    notes: ''
  });

  // Calculate total earned from paid invoices
  const totalEarned = invoices
    .filter(inv => inv.status === 'paid')
    .reduce((sum, inv) => sum + (inv.total_amount || 0), 0);

  // Calculate days remaining until payment is due
  const getPaymentDueDays = (invoice) => {
    if (!invoice || invoice.status === 'paid') return null;
    
    const paymentTerms = invoice.payment_terms || '30 Days';
    const daysToAdd = parseInt(paymentTerms) || 30;
    
    const createdDate = new Date(invoice.created_at);
    const dueDate = new Date(createdDate);
    dueDate.setDate(dueDate.getDate() + daysToAdd);
    
    const today = new Date();
    const diffTime = dueDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    getSubbieId(userData);
  }, []);

  const getSubbieId = async (userData) => {
    try {
      let savedId = localStorage.getItem('subbieId');
      if (savedId) {
        setSubbieId(savedId);
        fetchData(savedId);
        return;
      }

      const response = await fetch(`${API_URL}/subcontractors?user_id=${userData.id}`);
      const data = await response.json();
      if (data.success && data.subcontractors && data.subcontractors.length > 0) {
        const id = data.subcontractors[0].id;
        setSubbieId(id);
        localStorage.setItem('subbieId', id);
        fetchData(id);
      } else {
        setLoading(false);
        toast.error('No subcontractor profile found');
      }
    } catch (err) {
      console.error('Error fetching subbie ID:', err);
      toast.error('Failed to load profile');
      setLoading(false);
    }
  };

  const fetchData = async (id) => {
    setLoading(true);
    setError(null);
    try {
      // Fetch invoices
      const invRes = await fetch(`${API_URL}/subbie-invoices/subbie/${id}`);
      const invData = await invRes.json();
      if (invData.success) {
        setInvoices(invData.invoices || []);
      }

      // Fetch pending loads for POD submission
      const pendingRes = await fetch(`${API_URL}/subbie-invoices/pending-pod/${id}`);
      const pendingData = await pendingRes.json();
      if (pendingData.success) {
        setPendingLoads(pendingData.loads || []);
      }

      // Fetch POD submissions
      const podRes = await fetch(`${API_URL}/subbie-invoices/pod-submissions/${id}`);
      const podData = await podRes.json();
      if (podData.success) {
        setPodSubmissions(podData.submissions || []);
      }

      // Fetch counts
      const countRes = await fetch(`${API_URL}/subbie-invoices/count/${id}`);
      const countData = await countRes.json();
      if (countData.success) {
        setCounts({
          pending: countData.pending || 0,
          paid: countData.paid || 0,
          readyForPOD: countData.readyForPOD || 0,
          podPending: countData.podPending || 0,
          total: countData.total || 0
        });
      }

    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message || 'Failed to load data');
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredInvoices = () => {
    let filtered = [...invoices];
    
    if (searchTerm) {
      filtered = filtered.filter(i => 
        i.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        i.job?.job_number?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (filter === 'pending') {
      filtered = filtered.filter(i => i.status === 'pending_payment');
    } else if (filter === 'paid') {
      filtered = filtered.filter(i => i.status === 'paid');
    }
    
    if (!showPaid) {
      filtered = filtered.filter(i => i.status !== 'paid');
    }
    
    return filtered;
  };

  const filteredInvoices = getFilteredInvoices();

  const handleCreateInvoice = async () => {
    if (!invoiceForm.weight || !invoiceForm.rate) {
      toast.error('Please enter weight and rate');
      return;
    }

    if (!selectedLoad) {
      toast.error('No load selected');
      return;
    }

    setUploading(true);
    try {
      const baseAmount = parseFloat(invoiceForm.weight) * parseFloat(invoiceForm.rate);
      const extrasTotal = invoiceForm.extras.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
      const subtotal = baseAmount + extrasTotal;
      
      // Calculate VAT
      const vatApplicable = invoiceForm.vatApplicable !== false;
      const vatAmount = vatApplicable ? (subtotal * 0.15) : 0;
      const totalAmount = subtotal + vatAmount;

      const response = await fetch(`${API_URL}/subbie-invoices/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          allocation_id: selectedLoad.id,
          job_id: selectedLoad.job_id,
          subcontractor_id: subbieId,
          weight_mt: parseFloat(invoiceForm.weight),
          rate_per_mt: parseFloat(invoiceForm.rate),
          base_amount: baseAmount,
          extras: invoiceForm.extras,
          subtotal: subtotal,
          vat_applicable: vatApplicable,
          vat_amount: vatAmount,
          total_amount: totalAmount,
          payment_terms: invoiceForm.paymentTerms
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('✅ Invoice created successfully!');
        setShowInvoiceModal(false);
        setSelectedLoad(null);
        setInvoiceForm({ 
          weight: '', 
          rate: '', 
          extras: [], 
          paymentTerms: '30 Days',
          vatApplicable: true 
        });
        fetchData(subbieId);
      } else {
        toast.error(data.error || 'Failed to create invoice');
      }
    } catch (err) {
      console.error('Error creating invoice:', err);
      toast.error('Failed to create invoice');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmitPOD = async () => {
    if (!podForm.submissionType) {
      toast.error('Please select submission method');
      return;
    }

    if (podForm.submissionType === 'courier' && !podForm.courierName) {
      toast.error('Please enter courier name');
      return;
    }

    if (!selectedLoad) {
      toast.error('No load selected');
      return;
    }

    setUploading(true);
    try {
      const response = await fetch(`${API_URL}/subbie-invoices/submit-pod`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          allocation_id: selectedLoad.id,
          subcontractor_id: subbieId,
          submission_type: podForm.submissionType,
          courier_name: podForm.courierName || null,
          waybill_number: podForm.waybillNumber || null,
          tracking_url: podForm.trackingUrl || null,
          notes: podForm.notes || null
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('✅ POD submitted successfully!');
        setShowPODModal(false);
        setSelectedLoad(null);
        setPodForm({ submissionType: 'courier', courierName: '', waybillNumber: '', trackingUrl: '', notes: '' });
        fetchData(subbieId);
      } else {
        toast.error(data.error || 'Failed to submit POD');
      }
    } catch (err) {
      console.error('Error submitting POD:', err);
      toast.error('Failed to submit POD');
    } finally {
      setUploading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'pending_payment': 'bg-yellow-100 text-yellow-700',
      'paid': 'bg-green-100 text-green-700'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-700'}`;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending_payment': '⏳ Pending Payment',
      'paid': '✅ Paid'
    };
    return labels[status] || status;
  };

  const getPodStatusBadge = (status) => {
    const styles = {
      'pending': 'bg-gray-100 text-gray-600',
      'pending_receipt': 'bg-purple-100 text-purple-700',
      'submitted': 'bg-purple-100 text-purple-700',
      'received': 'bg-green-100 text-green-700'
    };
    return `px-2 py-0.5 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-600'}`;
  };

  const getPodStatusLabel = (status) => {
    const labels = {
      'pending': '⏳ Pending Submission',
      'pending_receipt': '⏳ Pending Receipt',
      'submitted': '📦 POD Submitted',
      'received': '✅ POD Received'
    };
    return labels[status] || status;
  };

  const getSubmissionTypeLabel = (type) => {
    return type === 'courier' ? '📦 Courier' : '📋 Manual Delivery';
  };

  const addExtra = () => {
    setInvoiceForm(prev => ({
      ...prev,
      extras: [...prev.extras, { description: '', amount: 0 }]
    }));
  };

  const removeExtra = (index) => {
    setInvoiceForm(prev => ({
      ...prev,
      extras: prev.extras.filter((_, i) => i !== index)
    }));
  };

  const updateExtra = (index, field, value) => {
    setInvoiceForm(prev => ({
      ...prev,
      extras: prev.extras.map((e, i) => i === index ? { ...e, [field]: value } : e)
    }));
  };

  const calculateTotal = () => {
    const baseAmount = parseFloat(invoiceForm.weight || 0) * parseFloat(invoiceForm.rate || 0);
    const extrasTotal = invoiceForm.extras.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
    const subtotal = baseAmount + extrasTotal;
    const vatAmount = invoiceForm.vatApplicable ? (subtotal * 0.15) : 0;
    return { subtotal, vatAmount, total: subtotal + vatAmount };
  };

  const formatCurrency = (amount) => {
    return `R${(amount || 0).toFixed(2)}`;
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilter('all');
    setShowPaid(false);
  };

  if (loading) {
    return (
      <SubbieLayout activeTab="invoices" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <FaSpinner className="animate-spin text-4xl text-green-500 mx-auto mb-4" />
            <p className="text-gray-500">Loading invoices...</p>
          </div>
        </div>
      </SubbieLayout>
    );
  }

  if (error) {
    return (
      <SubbieLayout activeTab="invoices" onLogout={onLogout} user={user}>
        <div className="bg-white rounded-xl p-8 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-red-600 mb-2">Error Loading Invoices</h2>
          <p className="text-gray-500">{error}</p>
          <button 
            onClick={() => fetchData(subbieId)} 
            className="mt-4 px-4 py-2 rounded-lg text-white" 
            style={{ backgroundColor: '#00A86B' }}
          >
            Retry
          </button>
        </div>
      </SubbieLayout>
    );
  }

  // ✅ Separate POD submissions by status
  const pendingPODs = podSubmissions.filter(p => p.status === 'pending_receipt' || p.status === 'submitted' || p.status === 'pending');

  // ✅ ONLY show received PODs that do NOT have an invoice yet
  const readyToInvoicePODs = podSubmissions.filter(p => {
    if (p.status !== 'received') return false;
    const hasInvoice = invoices.some(inv => inv.allocation_id === p.allocation_id);
    return !hasInvoice;
  });

  return (
    <SubbieLayout activeTab="invoices" onLogout={onLogout} user={user}>
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-navy">📄 Invoices</h1>
          <p className="text-sm text-gray-500">
            {filteredInvoices.length} invoices • {counts.pending} pending • {counts.paid} paid
          </p>
        </div>
        <button
          onClick={() => fetchData(subbieId)}
          className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg text-sm hover:bg-green hover:text-navy transition-colors"
        >
          <FaSync size={14} /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border p-4 shadow-sm" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-green-50">
              <FaMoneyBillWave className="text-green-600" size={18} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Total Earned</p>
              <p className="text-xl font-bold text-green-600">{formatCurrency(totalEarned)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4 shadow-sm" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-yellow-50">
              <FaHourglassHalf className="text-yellow-600" size={18} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Pending Payment</p>
              <p className="text-xl font-bold text-navy">{counts.pending}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4 shadow-sm" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-purple-50">
              <FaFileAlt className="text-purple-600" size={18} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Ready for POD</p>
              <p className="text-xl font-bold text-navy">{counts.readyForPOD}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4 shadow-sm" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-50">
              <FaCheckCircle className="text-blue-600" size={18} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Paid Invoices</p>
              <p className="text-xl font-bold text-navy">{counts.paid}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border p-4 mb-6" style={{ borderColor: '#E8ECEF' }}>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <FaFilter className="text-gray-400" />
            <span className="text-sm font-medium text-gray-600">Filters:</span>
          </div>
          
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${filter === 'all' ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${filter === 'pending' ? 'bg-yellow-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Pending Payment
          </button>
          <button
            onClick={() => setFilter('paid')}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${filter === 'paid' ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Paid
          </button>
          
          <div className="flex items-center gap-2 ml-2">
            <input
              type="checkbox"
              checked={showPaid}
              onChange={(e) => setShowPaid(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
            />
            <label className="text-sm text-gray-600">Show Paid</label>
          </div>
          
          <div className="flex-1"></div>
          
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
            <input
              type="text"
              placeholder="Search invoices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:border-green w-48"
              style={{ borderColor: '#E8ECEF' }}
            />
          </div>
          
          <button
            onClick={clearFilters}
            className="text-sm text-red-500 hover:text-red-700"
          >
            Clear All
          </button>
        </div>
      </div>

      {/* SECTION 1: READY FOR POD SUBMISSION */}
      {pendingLoads.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-navy mb-3">📦 Ready for POD Submission ({pendingLoads.length})</h2>
          <div className="space-y-3">
            {pendingLoads.map((load) => (
              <div key={load.id} className="bg-white rounded-xl border p-4 shadow-sm hover:shadow-md transition-shadow" style={{ borderColor: '#E8ECEF' }}>
                <div className="flex flex-wrap justify-between items-center gap-4">
                  <div>
                    <p className="font-bold text-navy">{load.job?.job_number || 'Unknown Job'}</p>
                    <p className="text-sm text-gray-500">
                      {load.truck?.registration_number || 'No Truck'} • {load.weighbridge_weight_mt || 0} MT
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setSelectedLoad(load);
                        setShowPODModal(true);
                      }}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors flex items-center gap-2 shadow-sm"
                    >
                      <FaUpload size={14} /> Submit POD
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SECTION 2: POD SUBMISSIONS - Pending receipt ONLY */}
      {pendingPODs.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-navy mb-3">📦 POD Submissions ({pendingPODs.length})</h2>
          <div className="space-y-3">
            {pendingPODs.map((pod) => {
              const invoice = invoices.find(i => i.allocation_id === pod.allocation_id);
              const hasInvoice = !!invoice;
              
              return (
                <div key={pod.id} className="bg-white rounded-xl border p-4 shadow-sm hover:shadow-md transition-shadow" style={{ borderColor: '#E8ECEF' }}>
                  <div className="flex flex-wrap justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <p className="font-bold text-navy">
                          {pod.allocation?.job?.job_number || 'Unknown Job'}
                        </p>
                        <span className={getPodStatusBadge(pod.status)}>
                          {getPodStatusLabel(pod.status)}
                        </span>
                        <span className="text-xs text-gray-400">
                          {getSubmissionTypeLabel(pod.submission_type)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">
                        {pod.allocation?.truck?.registration_number || 'No Truck'} • {pod.allocation?.weighbridge_weight_mt || 0} MT
                      </p>
                      {pod.submission_type === 'courier' && (
                        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mt-1">
                          {pod.courier_name && <span>📦 {pod.courier_name}</span>}
                          {pod.waybill_number && <span>📋 Waybill: {pod.waybill_number}</span>}
                          {pod.tracking_url && (
                            <a href={pod.tracking_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center gap-1">
                              <FaExternalLinkAlt size={10} /> Track
                            </a>
                          )}
                        </div>
                      )}
                      {pod.submission_type === 'manual' && (
                        <p className="text-xs text-gray-400 mt-1">📋 Manual delivery - awaiting receipt at HO</p>
                      )}
                      {pod.notes && <p className="text-xs text-gray-400 mt-1">📝 {pod.notes}</p>}
                      <div className="text-xs text-gray-400 mt-1">
                        Submitted: {new Date(pod.submitted_at).toLocaleDateString()} at {new Date(pod.submitted_at).toLocaleTimeString()}
                      </div>
                      {hasInvoice && (
                        <p className="text-xs text-blue-600 mt-1">📄 Invoice created: {invoice.invoice_number}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {pod.tracking_url && (
                        <a
                          href={pod.tracking_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs transition-colors flex items-center gap-1 shadow-sm"
                        >
                          <FaExternalLinkAlt size={12} /> Track
                        </a>
                      )}
                      {hasInvoice && (
                        <button
                          onClick={() => {
                            setSelectedInvoice(invoice);
                            setShowDetailModal(true);
                          }}
                          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs transition-colors flex items-center gap-1 shadow-sm"
                        >
                          <FaEye size={12} /> View Invoice
                        </button>
                      )}
                      {hasInvoice && invoice.status === 'paid' && (
                        <span className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-xs flex items-center gap-1">
                          <FaCheckCircle size={12} /> Paid
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* SECTION 3: READY TO INVOICE - ONLY SHOWS IF NO INVOICE EXISTS */}
      {readyToInvoicePODs.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-navy mb-3">📦 Ready to Invoice ({readyToInvoicePODs.length})</h2>
          <div className="space-y-3">
            {readyToInvoicePODs.map((pod) => {
              return (
                <div key={pod.id} className="bg-white rounded-xl border p-4 shadow-sm hover:shadow-md transition-shadow" style={{ borderColor: '#E8ECEF' }}>
                  <div className="flex flex-wrap justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <p className="font-bold text-navy">
                          {pod.allocation?.job?.job_number || 'Unknown Job'}
                        </p>
                        <span className={getPodStatusBadge(pod.status)}>
                          {getPodStatusLabel(pod.status)}
                        </span>
                        <span className="text-xs text-gray-400">
                          {getSubmissionTypeLabel(pod.submission_type)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">
                        {pod.allocation?.truck?.registration_number || 'No Truck'} • {pod.allocation?.weighbridge_weight_mt || 0} MT
                      </p>
                      {pod.submission_type === 'courier' && (
                        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mt-1">
                          {pod.courier_name && <span>📦 {pod.courier_name}</span>}
                          {pod.waybill_number && <span>📋 Waybill: {pod.waybill_number}</span>}
                          {pod.tracking_url && (
                            <a href={pod.tracking_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center gap-1">
                              <FaExternalLinkAlt size={10} /> Track
                            </a>
                          )}
                        </div>
                      )}
                      {pod.submission_type === 'manual' && (
                        <p className="text-xs text-gray-400 mt-1">📋 Manual delivery - received at HO</p>
                      )}
                      {pod.notes && <p className="text-xs text-gray-400 mt-1">📝 {pod.notes}</p>}
                      <div className="text-xs text-gray-400 mt-1">
                        Received: {new Date(pod.received_at || pod.updated_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          const load = pod.allocation;
                          setSelectedLoad(load);
                          setInvoiceForm({
                            weight: load?.weighbridge_weight_mt || '',
                            rate: load?.job?.subbie_target_price || '',
                            extras: [],
                            paymentTerms: '30 Days',
                            vatApplicable: true
                          });
                          setShowInvoiceModal(true);
                        }}
                        className="px-4 py-2 text-white rounded-lg text-sm transition-colors flex items-center gap-2 shadow-md"
                        style={{
                          backgroundColor: '#00A86B',
                          border: 'none',
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#00995E'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#00A86B'}
                      >
                        <FaFileInvoice size={14} /> Create Invoice
                      </button>
                      {pod.tracking_url && (
                        <a
                          href={pod.tracking_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs transition-colors flex items-center gap-1 shadow-sm"
                        >
                          <FaExternalLinkAlt size={12} /> Track
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* INVOICES LIST */}
      {filteredInvoices.length === 0 && pendingLoads.length === 0 && pendingPODs.length === 0 && readyToInvoicePODs.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-6xl mb-4">📄</div>
          <h3 className="text-xl font-semibold text-navy">No Invoices Yet</h3>
          <p className="text-sm text-gray-500">
            You don't have any invoices yet. Complete loads and submit PODs to start invoicing.
          </p>
        </div>
      ) : filteredInvoices.length === 0 && (pendingLoads.length > 0 || pendingPODs.length > 0 || readyToInvoicePODs.length > 0) ? (
        <div className="bg-white rounded-xl p-8 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-4xl mb-4">📦</div>
          <h3 className="text-lg font-semibold text-navy">No Invoices Created Yet</h3>
          <p className="text-sm text-gray-500">
            {pendingLoads.length > 0 && `You have ${pendingLoads.length} load(s) ready for POD submission. `}
            {pendingPODs.length > 0 && `You have ${pendingPODs.length} POD(s) pending receipt. `}
            {readyToInvoicePODs.length > 0 && `You have ${readyToInvoicePODs.length} load(s) ready to invoice.`}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredInvoices.map((invoice) => {
            const isPaid = invoice.status === 'paid';
            const isPending = invoice.status === 'pending_payment';
            const podStatus = invoice.pod_status || 'pending';
            const podSubmission = podSubmissions.find(p => p.allocation_id === invoice.allocation_id);
            const daysLeft = getPaymentDueDays(invoice);
            
            return (
              <div key={invoice.id} className={`bg-white rounded-xl border p-6 shadow-sm hover:shadow-md transition-shadow ${isPaid ? 'opacity-80' : ''}`} style={{ borderColor: '#E8ECEF' }}>
                <div className="flex flex-wrap justify-between items-start gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-navy">{invoice.invoice_number}</h3>
                      <span className={getStatusBadge(invoice.status)}>{getStatusLabel(invoice.status)}</span>
                      <span className={getPodStatusBadge(podStatus)}>
                        POD: {getPodStatusLabel(podStatus)}
                      </span>
                      {podSubmission && podSubmission.submission_type === 'courier' && podSubmission.courier_name && (
                        <span className="text-xs text-gray-400">
                          📦 {podSubmission.courier_name}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      <FaBuilding className="inline mr-1" /> {invoice.job?.job_number || 'N/A'}
                    </p>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500 mt-2">
                      <span className="flex items-center gap-1">
                        <FaWeightHanging size={14} /> {invoice.weight_mt} MT
                      </span>
                      <span className="flex items-center gap-1">
                        <FaDollarSign size={14} /> R{invoice.rate_per_mt}/MT
                      </span>
                      <span className="flex items-center gap-1 font-medium text-navy">
                        <FaMoneyBillWave size={14} /> {formatCurrency(invoice.total_amount)}
                      </span>
                      <span className="flex items-center gap-1">
                        <FaCalendarAlt size={14} /> {new Date(invoice.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    {podSubmission && podSubmission.submission_type === 'courier' && podSubmission.waybill_number && (
                      <div className="mt-1 text-xs text-gray-400">
                        Waybill: {podSubmission.waybill_number}
                        {podSubmission.tracking_url && (
                          <a
                            href={podSubmission.tracking_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-2 text-blue-600 hover:text-blue-800"
                          >
                            Track <FaExternalLinkAlt className="inline ml-0.5" size={10} />
                          </a>
                        )}
                      </div>
                    )}
                    {isPending && (
                      <div className="mt-2 text-sm text-gray-500">
                        <FaClock className="inline mr-1 text-yellow-500" />
                        Payment Terms: {invoice.payment_terms || '30 Days'}
                      </div>
                    )}
                    
                    {/* ✅ PAYMENT DUE COUNTDOWN TIMER - FIXED TEXT */}
                    {isPending && daysLeft !== null && (
                      <div className="mt-1 text-sm">
                        {daysLeft > 0 ? (
                          <span className="text-blue-600 flex items-center gap-1">
                            <FaClock size={12} /> Payment due in {daysLeft} days
                          </span>
                        ) : daysLeft === 0 ? (
                          <span className="text-orange-500 font-medium flex items-center gap-1">
                            ⚠️ Payment due today!
                          </span>
                        ) : (
                          <span className="text-red-600 font-medium flex items-center gap-1">
                            ⚠️ Payment overdue by {Math.abs(daysLeft)} days!
                          </span>
                        )}
                      </div>
                    )}

                    {isPaid && invoice.payment_date && (
                      <div className="mt-2 text-sm text-green-600">
                        <FaCheckCircle className="inline mr-1" />
                        Paid on: {new Date(invoice.payment_date).toLocaleDateString()}
                        {invoice.payment_method && ` • ${invoice.payment_method}`}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => {
                        setSelectedInvoice(invoice);
                        setShowDetailModal(true);
                      }}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs transition-colors flex items-center gap-1 shadow-sm"
                    >
                      <FaEye size={12} /> View
                    </button>
                    
                    {/* Download PDF button - only when paid */}
                    {isPaid && (
                      <InvoicePDFButton 
                        invoice={invoice}
                        job={invoice.job}
                        subbie={invoice.subcontractor}
                      />
                    )}

                    {isPaid && invoice.pop_url && (
                      <a
                        href={invoice.pop_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 bg-green hover:bg-green text-white rounded-lg text-xs transition-colors flex items-center gap-1 shadow-sm"
                      >
                        <FaDownload size={12} /> POP
                      </a>
                    )}
                    {podSubmission && podSubmission.tracking_url && (
                      <a
                        href={podSubmission.tracking_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs transition-colors flex items-center gap-1 shadow-sm"
                      >
                        <FaExternalLinkAlt size={12} /> Track
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* CREATE INVOICE MODAL */}
      {showInvoiceModal && selectedLoad && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowInvoiceModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 pb-4 border-b" style={{ borderColor: '#E8ECEF' }}>
              <h2 className="text-xl font-bold text-navy">📄 Create Invoice</h2>
              <button onClick={() => setShowInvoiceModal(false)} className="text-gray-500 hover:text-navy">
                <FaTimes size={20} />
              </button>
            </div>

            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Load:</span> {selectedLoad.job?.job_number || 'N/A'}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Truck:</span> {selectedLoad.truck?.registration_number || 'N/A'}
              </p>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Weight (MT) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={invoiceForm.weight}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, weight: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                    placeholder="e.g. 33.8"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rate (R/MT) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={invoiceForm.rate}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, rate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                    placeholder="e.g. 540"
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">Extras</label>
                  <button
                    onClick={addExtra}
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    <FaPlus size={12} /> Add Extra
                  </button>
                </div>
                {invoiceForm.extras.map((extra, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={extra.description}
                      onChange={(e) => updateExtra(index, 'description', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green text-sm"
                      placeholder="Description (e.g. Demurage)"
                    />
                    <input
                      type="number"
                      step="0.01"
                      value={extra.amount}
                      onChange={(e) => updateExtra(index, 'amount', e.target.value)}
                      className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green text-sm"
                      placeholder="Amount"
                    />
                    <button
                      onClick={() => removeExtra(index)}
                      className="text-red-500 hover:text-red-700 px-2"
                    >
                      <FaTrash size={14} />
                    </button>
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
                <select
                  value={invoiceForm.paymentTerms}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, paymentTerms: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                >
                  <option value="7 Days">7 Days</option>
                  <option value="14 Days">14 Days</option>
                  <option value="30 Days">30 Days</option>
                  <option value="45 Days">45 Days</option>
                  <option value="60 Days">60 Days</option>
                </select>
              </div>

              {/* VAT Checkbox */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="vatCheckbox"
                    checked={invoiceForm.vatApplicable}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, vatApplicable: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  <label htmlFor="vatCheckbox" className="text-sm font-medium text-gray-700">
                    Charge VAT (15%)
                  </label>
                  <span className="text-xs text-gray-400 ml-auto">
                    {invoiceForm.vatApplicable ? '✅ VAT Applied' : '❌ Cross-border - No VAT'}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-1 ml-7">
                  Untick for cross-border jobs where VAT does not apply
                </p>
              </div>

              {/* Totals with VAT */}
              <div className="bg-gray-50 rounded-lg p-4">
                {(() => {
                  const { subtotal, vatAmount, total } = calculateTotal();
                  return (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Base Amount</span>
                        <span className="font-medium text-navy">{formatCurrency(parseFloat(invoiceForm.weight || 0) * parseFloat(invoiceForm.rate || 0))}</span>
                      </div>
                      {invoiceForm.extras.map((extra, index) => (
                        <div key={index} className="flex justify-between text-sm">
                          <span className="text-gray-600">{extra.description || `Extra ${index + 1}`}</span>
                          <span className="font-medium text-navy">{formatCurrency(parseFloat(extra.amount) || 0)}</span>
                        </div>
                      ))}
                      <div className="border-t mt-1 pt-1 flex justify-between text-sm">
                        <span className="text-gray-600 font-medium">Subtotal</span>
                        <span className="font-medium text-navy">{formatCurrency(subtotal)}</span>
                      </div>
                      {invoiceForm.vatApplicable && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">VAT (15%)</span>
                          <span className="font-medium text-navy">{formatCurrency(vatAmount)}</span>
                        </div>
                      )}
                      <div className="border-t mt-2 pt-2 flex justify-between font-bold">
                        <span className="text-navy">TOTAL</span>
                        <span className="text-green-600">{formatCurrency(total)}</span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t" style={{ borderColor: '#E8ECEF' }}>
              <button
                onClick={() => setShowInvoiceModal(false)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateInvoice}
                disabled={uploading}
                className="px-4 py-2 text-white rounded-lg text-sm transition-colors flex items-center gap-2 disabled:opacity-50 shadow-sm"
                style={{
                  backgroundColor: '#00A86B',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#00995E'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#00A86B'}
              >
                {uploading ? <FaSpinner className="animate-spin" /> : <FaFileInvoice size={14} />}
                {uploading ? 'Creating...' : 'Create Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SUBMIT POD MODAL */}
      {showPODModal && selectedLoad && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowPODModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="flex justify-between items-center mb-4 pb-4 border-b" style={{ borderColor: '#E8ECEF' }}>
              <h2 className="text-xl font-bold text-navy">📦 Submit Original POD</h2>
              <button onClick={() => setShowPODModal(false)} className="text-gray-500 hover:text-navy">
                <FaTimes size={20} />
              </button>
            </div>

            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Load:</span> {selectedLoad.job?.job_number || 'N/A'}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Truck:</span> {selectedLoad.truck?.registration_number || 'N/A'}
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Submission Method *</label>
                <select
                  value={podForm.submissionType}
                  onChange={(e) => setPodForm({ ...podForm, submissionType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                >
                  <option value="courier">Courier</option>
                  <option value="manual">Manual Delivery</option>
                </select>
              </div>

              {podForm.submissionType === 'courier' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Courier Name *</label>
                    <input
                      type="text"
                      value={podForm.courierName}
                      onChange={(e) => setPodForm({ ...podForm, courierName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                      placeholder="e.g. DHL, FedEx"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Waybill Number</label>
                    <input
                      type="text"
                      value={podForm.waybillNumber}
                      onChange={(e) => setPodForm({ ...podForm, waybillNumber: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                      placeholder="e.g. 123456789"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tracking URL</label>
                    <input
                      type="url"
                      value={podForm.trackingUrl}
                      onChange={(e) => setPodForm({ ...podForm, trackingUrl: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                      placeholder="https://track.courier.com/..."
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={podForm.notes}
                  onChange={(e) => setPodForm({ ...podForm, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                  rows="2"
                  placeholder="Any additional notes..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t" style={{ borderColor: '#E8ECEF' }}>
              <button
                onClick={() => setShowPODModal(false)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitPOD}
                disabled={uploading}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors flex items-center gap-2 disabled:opacity-50 shadow-sm"
              >
                {uploading ? <FaSpinner className="animate-spin" /> : <FaUpload size={14} />}
                {uploading ? 'Submitting...' : 'Submit POD'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* VIEW INVOICE DETAIL MODAL */}
      {showDetailModal && selectedInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowDetailModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 pb-4 border-b" style={{ borderColor: '#E8ECEF' }}>
              <h2 className="text-xl font-bold text-navy">📄 Invoice Details</h2>
              <button onClick={() => setShowDetailModal(false)} className="text-gray-500 hover:text-navy">
                <FaTimes size={20} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-xs text-gray-500">Invoice Number</p>
                <p className="font-medium text-navy">{selectedInvoice.invoice_number}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Status</p>
                <span className={getStatusBadge(selectedInvoice.status)}>{getStatusLabel(selectedInvoice.status)}</span>
              </div>
              <div>
                <p className="text-xs text-gray-500">Job Number</p>
                <p className="font-medium text-navy">{selectedInvoice.job?.job_number || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Created</p>
                <p className="font-medium text-navy">{new Date(selectedInvoice.created_at).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Weight</p>
                <p className="font-medium text-navy">{selectedInvoice.weight_mt} MT</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Rate</p>
                <p className="font-medium text-navy">R{selectedInvoice.rate_per_mt}/MT</p>
              </div>
              {selectedInvoice.vat_applicable !== undefined && (
                <>
                  <div>
                    <p className="text-xs text-gray-500">VAT Applied</p>
                    <p className="font-medium text-navy">{selectedInvoice.vat_applicable ? '✅ Yes (15%)' : '❌ No'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">VAT Amount</p>
                    <p className="font-medium text-navy">{formatCurrency(selectedInvoice.vat_amount || 0)}</p>
                  </div>
                </>
              )}
            </div>

            <div className="border-t pt-4" style={{ borderColor: '#E8ECEF' }}>
              <h3 className="font-medium text-navy mb-2">Amount Breakdown</h3>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Base Amount ({selectedInvoice.weight_mt} MT × R{selectedInvoice.rate_per_mt})</span>
                  <span className="font-medium">{formatCurrency(selectedInvoice.base_amount)}</span>
                </div>
                {selectedInvoice.extras && selectedInvoice.extras.map((extra, idx) => (
                  <div key={idx} className="flex justify-between">
                    <span className="text-gray-600">{extra.description || `Extra ${idx + 1}`}</span>
                    <span className="font-medium">{formatCurrency(extra.amount)}</span>
                  </div>
                ))}
                {selectedInvoice.subtotal !== undefined && (
                  <div className="flex justify-between border-t pt-1 mt-1">
                    <span className="text-gray-600 font-medium">Subtotal</span>
                    <span className="font-medium">{formatCurrency(selectedInvoice.subtotal)}</span>
                  </div>
                )}
                {selectedInvoice.vat_applicable && selectedInvoice.vat_amount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">VAT (15%)</span>
                    <span className="font-medium">{formatCurrency(selectedInvoice.vat_amount)}</span>
                  </div>
                )}
                <div className="border-t pt-2 mt-2 flex justify-between font-bold">
                  <span className="text-navy">Total</span>
                  <span className="text-green-600">{formatCurrency(selectedInvoice.total_amount)}</span>
                </div>
              </div>
            </div>

            {selectedInvoice.payment_date && (
              <div className="mt-4 p-3 bg-green-50 rounded-lg">
                <p className="text-sm text-green-700">
                  <FaCheckCircle className="inline mr-2" />
                  Paid on: {new Date(selectedInvoice.payment_date).toLocaleDateString()}
                  {selectedInvoice.payment_method && ` • ${selectedInvoice.payment_method}`}
                </p>
                {selectedInvoice.pop_url && (
                  <a
                    href={selectedInvoice.pop_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 underline mt-1 inline-block"
                  >
                    View POP
                  </a>
                )}
              </div>
            )}

            {/* ✅ PAYMENT DUE COUNTDOWN IN DETAIL MODAL - FIXED TEXT */}
            {selectedInvoice.status === 'pending_payment' && (
              <div className="mt-4">
                {(() => {
                  const daysLeft = getPaymentDueDays(selectedInvoice);
                  if (daysLeft === null) return null;
                  const dueDate = new Date(new Date(selectedInvoice.created_at).getTime() + (parseInt(selectedInvoice.payment_terms || '30') * 24 * 60 * 60 * 1000)).toLocaleDateString();
                  if (daysLeft > 0) {
                    return (
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-sm text-blue-700 flex items-center gap-2">
                          <FaClock className="text-blue-600" />
                          Payment due in {daysLeft} days (due {dueDate})
                        </p>
                      </div>
                    );
                  } else if (daysLeft === 0) {
                    return (
                      <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                        <p className="text-sm text-orange-700 font-medium flex items-center gap-2">
                          ⚠️ Payment due today!
                        </p>
                      </div>
                    );
                  } else {
                    return (
                      <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                        <p className="text-sm text-red-700 font-medium flex items-center gap-2">
                          ⚠️ Payment overdue by {Math.abs(daysLeft)} days!
                        </p>
                      </div>
                    );
                  }
                })()}
              </div>
            )}

            {/* Download PDF button in detail modal */}
            {selectedInvoice.status === 'paid' && (
              <div className="mt-4 flex justify-end">
                <InvoicePDFButton 
                  invoice={selectedInvoice}
                  job={selectedInvoice.job}
                  subbie={selectedInvoice.subcontractor}
                />
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t" style={{ borderColor: '#E8ECEF' }}>
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 bg-navy hover:bg-green hover:text-navy text-white rounded-lg text-sm transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </SubbieLayout>
  );
}

export default SubbieInvoices;
